import { createTheme, type ThemeOptions } from '@mui/material';
// @ts-ignore
import defaultTheme from '@mui/material/styles/defaultTheme';

const secondary = '#fff';
const primary = '#197CB3';
const primaryControlColor = '#197CB3';
const primaryControlHoverColor = 'rgb(17, 86, 125)';
const primaryCardBackground = '#F6FAFC';
const success = '#2E7D32';
const info = '#6c6d6e';
const warning = '#E65100';
const error = '#C62828';
const statusOnboarding = '#2E7D32';
const statusActive = '#01579B';
const statusBackground = `rgba(2, 136, 209, 0.12)`;
const statusInactiveBackground = `rgba(1, 16, 18, 0.08)`;
const headerBackground = 'rgba(1, 16, 18, 0.90)';
const navTextColor = 'rgba(255, 255, 255, 0.75)';
const navTextHoverColor = '#FFF';
const navTextActiveColor = '#FFF';
const footerBackground = 'rgba(1, 16, 18, 0.90)';
const footerTextColor = '#FFF';
const iconSecondaryColor = '#01101299';
const lightTextPrimary = 'rgba(1, 16, 18, 0.90)';
const lightTextSecondary = 'rgb(103, 112, 113)';
const lightTextDisabled = 'rgb(140, 147, 148)';
const lightErrorMain = 'rgb(211, 47, 47)';
const lightDividerColor = 'rgba(0, 0, 0, 0.12)';
const lightActionDisabled90p = 'rgba(1, 16, 18, 0.90)';
const lightActionDisabled60p = 'rgba(1, 16, 18, 0.60)';
const lightActionDisabled54p = 'rgba(1, 16, 18, 0.54)';
const lightActionDisabled45p = 'rgba(1, 16, 18, 0.45)';
const lightActionDisabled26p = 'rgba(1, 16, 18, 0.26)';
const lightActionDisabled15p = 'rgba(1, 16, 18, 0.15)';
const lightActionDisabled12p = 'rgba(1, 16, 18, 0.12)';
const lightActionDisabled08p = 'rgba(1, 16, 18, 0.08)';
const lightActionDisabled04p = 'rgba(1, 16, 18, 0.04)';
const lightPrimaryShades12p = '#0288d11f';
const lightPrimaryShades08p = 'rgba(25, 124, 179, 0.08)';
const lightPrimaryShades04p =
  'linear-gradient(0deg, rgba(25, 124, 179, 0.04) 0%, rgba(25, 124, 179, 0.04) 100%)';
const lightGrayToTransparentGradient =
  'linear-gradient(90deg,rgba(241, 241, 241, 1) 50%, rgba(241, 241, 241, 0) 85%)';
const river = '#3D7494';
const slate = '#475057';
const white04p = 'rgba(255, 255, 255, 0.04)';
const whiteOverlay90p = 'rgba(255, 255, 255, 0.9)';
const statusInactive = '#011012';
const statusDraft = '#011012';
const lightSecondaryPrimarySubtle = 'rgb(83, 93, 94)';
const lightBackgroundPaperHover = 'rgb(245, 245, 246)';

const shadows = [...defaultTheme.shadows];
shadows[2] = '0px 2px 7px 0px rgba(1, 16, 18, 0.15)'; //Elevation2
shadows[3] = '0px 4px 8px 0px rgba(1, 16, 18, 0.15)'; //Elevation3
shadows[16] = `
rgb(255, 255, 255) 0px 0px 0px 0px,
rgba(0, 0, 0, 0.05) 0px 0px 0px 1px,
rgba(0, 0, 0, 0.1) 0px 10px 15px -3px,
rgba(0, 0, 0, 0.05) 0px 4px 6px -2px
`; //Elevation16
const darkShadow30p = 'rgba(0, 0, 0, 0.3)';
const lightWarningMain = '#ED6C02';
const lightWarningShades12p = 'rgba(237, 108, 2, 0.12)';
const lightInfoMain = '#0288D1';
const lightInfoShades12p = 'rgba(2, 136, 209, 0.12)';
const lightInfoShades04p = 'rgba(2, 136, 209, 0.04)';
const lightSuccessMain = '#2E7D32';
const lightSuccessShades12p = 'rgba(46, 125, 50, 0.12)';
const molinaMain = '#06828F';
const lightPrimaryShades04pGradient90deg =
  'linear-gradient(90deg, rgba(25, 124, 179, 0.04) 0%, rgba(25, 124, 179, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)';

const palette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: primary,
  },
  secondary: {
    main: secondary,
  },
  info: {
    main: info,
  },
  success: {
    main: success,
  },
  warning: {
    main: warning,
  },
  error: {
    main: error,
    light: lightErrorMain,
  },
  statusOnboarding: {
    main: statusOnboarding,
  },
  statusActive: {
    main: statusActive,
  },
  statusBackground: {
    main: statusBackground,
  },
  statusInactive: {
    main: statusInactive,
  },
  statusDraft: {
    main: statusDraft,
  },
  statusInactiveBackground: {
    main: statusInactiveBackground,
  },
  buttonColor: {
    main: primaryControlColor,
  },
  buttonHoverColor: {
    main: primaryControlHoverColor,
  },
  headerBackground: {
    main: headerBackground,
  },
  navigationTextColor: {
    main: navTextColor,
  },
  navigationTextHoverColor: {
    main: navTextHoverColor,
  },
  navigationTextActiveColor: {
    main: navTextActiveColor,
  },
  footerBackground: {
    main: footerBackground,
  },
  footerTextColor: {
    main: footerTextColor,
  },
  iconSecondaryColor: {
    main: iconSecondaryColor,
  },
  lightTextPrimary: {
    main: lightTextPrimary,
  },
  lightTextSecondary: {
    main: lightTextSecondary,
  },
  lightTextDisabled: {
    main: lightTextDisabled,
  },
  lightDividerColor: {
    main: lightDividerColor,
  },
  lightActionDisabled90p: {
    main: lightActionDisabled90p,
  },
  lightActionDisabled60p: {
    main: lightActionDisabled60p,
  },
  lightActionDisabled54p: {
    main: lightActionDisabled54p,
  },
  lightActionDisabled45p: {
    main: lightActionDisabled45p,
  },
  lightActionDisabled26p: {
    main: lightActionDisabled26p,
  },
  lightActionDisabled15p: {
    main: lightActionDisabled15p,
  },
  lightActionDisabled12p: {
    main: lightActionDisabled12p,
  },
  lightActionDisabled08p: {
    main: lightActionDisabled08p,
  },
  lightActionDisabled04p: {
    main: lightActionDisabled04p,
  },
  lightPrimaryShades12p: {
    main: lightPrimaryShades12p,
  },
  lightPrimaryShades08p: {
    main: lightPrimaryShades08p,
  },
  lightPrimaryShades04p: {
    main: lightPrimaryShades04p,
  },
  lightGrayToTransparentGradient: {
    main: lightGrayToTransparentGradient,
  },
  river: {
    main: river,
  },
  slate: {
    main: slate,
  },
  white04p: {
    main: white04p,
  },
  primaryCardBackground: {
    main: primaryCardBackground,
  },
  whiteOverlay90p: {
    main: whiteOverlay90p,
  },
  lightSecondaryPrimarySubtle: {
    main: lightSecondaryPrimarySubtle,
  },
  lightBackgroundPaperHover: {
    main: lightBackgroundPaperHover,
  },
  darkShadow30p: {
    main: darkShadow30p,
  },
  lightWarningMain: {
    main: lightWarningMain,
  },
  lightWarningShades12p: {
    main: lightWarningShades12p,
  },
  lightInfoMain: {
    main: lightInfoMain,
  },
  lightInfoShades12p: {
    main: lightInfoShades12p,
  },
  lightInfoShades04p: {
    main: lightInfoShades04p,
  },
  lightSuccessMain: {
    main: lightSuccessMain,
  },
  lightSuccessShades12p: {
    main: lightSuccessShades12p,
  },
  molinaMain: {
    main: molinaMain,
  },
  lightPrimaryShades04pGradient90deg: {
    main: lightPrimaryShades04pGradient90deg,
  },
};

const paletteTheme = createTheme({
  palette,
  shadows,
} as ThemeOptions);

export { palette, paletteTheme };
